import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import {
  Text,
  Title,
  Stack,
  useMantineColorScheme,
  Box,
  SimpleGrid,
  Card,
  Group,
  RingProgress,
  Progress,
  Badge,
  Alert,
  ThemeIcon,
  Center,
  Loader,
  Modal,
  ActionIcon,
  Tooltip,
  Select,
} from '@mantine/core';
import { dashboardService } from '../services/dashboardService';
import { especialidadPerfilService, EspecialidadPerfilDTO } from '../services/especialidadPerfilService';
import { colorEspecialidad, capitalizarEspecialidad } from '../utils/especialidad';
import { AnalyticsStatsDTO, HistorialExamenDTO, DashboardStatsDTO, ProgresoAnalisisDTO, ComparativaDTO } from '../types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { dashboardStatsQuery, analyticsQuery, historialQuery, progresoEspecialidadQuery, comparativaQuery } from '../queries/appQueries';
import {
  IconTrophy,
  IconBrain,
  IconFlame,
  IconStar,
  IconChevronRight,
  IconTrendingUp,
  IconTrendingDown,
  IconChevronLeft,
  IconInfoCircle,
  IconRun,
  IconSettings,
  IconStethoscope,
  IconPill,
  IconHeartbeat,
  IconHeart,
  IconAmbulance,
  IconPalette,
  IconClock,
  IconCalendar,
  IconChartBar,
} from '@tabler/icons-react';

const EstadisticasPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const { collapsed, setCollapsed } = useSidebar();
  const [dashboardTab, setDashboardTab] = useState(0);
  const [expandedItems, setExpandedItems] = useState<{[key: number]: boolean}>({});
  const [calendarioModalOpen, setCalendarioModalOpen] = useState(false);
  const [calendarioMes, setCalendarioMes] = useState(new Date().getMonth() + 1);
  const [calendarioYear, setCalendarioYear] = useState(new Date().getFullYear());
  const [diasActivosCalendario, setDiasActivosCalendario] = useState<number[]>([]);
  const [showStreakAnimation, setShowStreakAnimation] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [modalJustOpened, setModalJustOpened] = useState(false);
  const [sidebarWasOpen, setSidebarWasOpen] = useState(false);
  
  // Especialidad seleccionada en la pestaña Comparativa.
  // null = usar la del perfil del usuario (default del backend).
  const [comparativaEspecialidad, setComparativaEspecialidad] = useState<string | null>(null);

  // Datos reales cacheados con React Query (instantáneo al revisitar; stats precargadas al login)
  const statsQ = useQuery(dashboardStatsQuery());
  const analyticsQ = useQuery(analyticsQuery());
  const historialQ = useQuery(historialQuery());
  const progresoQ = useQuery(progresoEspecialidadQuery());
  const comparativaQ = useQuery(comparativaQuery(comparativaEspecialidad ?? undefined));
  const especialidadesCatalogoQ = useQuery({
    queryKey: ['especialidadesPerfilActivas'],
    queryFn: (): Promise<EspecialidadPerfilDTO[]> => especialidadPerfilService.listarActivas(),
    staleTime: 60 * 60 * 1000,
  });
  const especialidadesCatalogo = (especialidadesCatalogoQ.data as EspecialidadPerfilDTO[] | undefined) ?? [];

  const dashboardStats: DashboardStatsDTO | null = statsQ.data ?? null;
  const analytics: AnalyticsStatsDTO | null = analyticsQ.data ?? null;
  const historial: HistorialExamenDTO | null = historialQ.data ?? null;
  const progreso: ProgresoAnalisisDTO | null = progresoQ.data ?? null;
  const comparativa: ComparativaDTO | null = comparativaQ.data ?? null;
  const loading = statsQ.isLoading || analyticsQ.isLoading || historialQ.isLoading || progresoQ.isLoading || comparativaQ.isLoading;
  const error = '';

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Función para obtener el icono correspondiente a cada especialidad
  const getEspecialidadIcon = (nombre: string) => {
    const nombreLower = nombre.toLowerCase();
    if (nombreLower.includes('cirugia') || nombreLower.includes('cirugía')) {
      return IconStethoscope;
    } else if (nombreLower.includes('ginecologia') || nombreLower.includes('ginecología') || nombreLower.includes('obstetricia')) {
      return IconHeart;
    } else if (nombreLower.includes('medicina interna') || nombreLower.includes('interna')) {
      return IconPill;
    } else if (nombreLower.includes('pediatria') || nombreLower.includes('pediatría')) {
      return IconHeartbeat;
    } else if (nombreLower.includes('urgencias') || nombreLower.includes('emergencias')) {
      return IconAmbulance;
    }
    return IconBrain; // Icono por defecto
  };

  // Función para asignar colores únicos a cada especialidad
  const getEspecialidadColor = (nombre: string, defaultColor: string) => {
    const nombreLower = nombre.toLowerCase();
    if (nombreLower.includes('cirugia') || nombreLower.includes('cirugía')) {
      return '#3b82f6'; // Azul
    } else if (nombreLower.includes('ginecologia') || nombreLower.includes('ginecología') || nombreLower.includes('obstetricia')) {
      return '#10b981'; // Verde
    } else if (nombreLower.includes('medicina interna') || nombreLower.includes('interna')) {
      return '#8b5cf6'; // Púrpura
    } else if (nombreLower.includes('pediatria') || nombreLower.includes('pediatría')) {
      return '#f59e0b'; // Naranja
    } else if (nombreLower.includes('urgencias') || nombreLower.includes('emergencias')) {
      return '#ef4444'; // Rojo
    }
    return defaultColor; // Color por defecto del backend
  };

  // Cargar datos reales al montar el componente
  // Cargar días activos del mes cuando se abre el modal
  useEffect(() => {
    const loadDiasActivos = async () => {
      if (calendarioModalOpen) {
        try {
          const data = await dashboardService.getCalendarioMes(calendarioYear, calendarioMes);
          setDiasActivosCalendario(data.diasActivos || []);
        } catch (error) {
          console.error('Error cargando días activos:', error);
        }
      }
    };
    loadDiasActivos();
  }, [calendarioModalOpen, calendarioMes, calendarioYear]);
  
  // Activar animación de racha cuando se abre el modal
  useEffect(() => {
    if (calendarioModalOpen) {
      // Delay pequeño para que el modal se renderice primero
      const timer = setTimeout(() => {
        setShowStreakAnimation(true);
        setTimeout(() => {
          setShowStreakAnimation(false);
        }, 1500);
      }, 50);
      
      return () => clearTimeout(timer);
    } else {
      setShowStreakAnimation(false);
      setModalJustOpened(false);
    }
  }, [calendarioModalOpen]);

  // Función para renderizar contenido según la pestaña activa del dashboard
  const renderDashboardTabContent = () => {
    // Paleta lowkey compartida (mismos tonos que la pestaña Comparativa)
    const lkAccent = colorScheme === 'dark' ? '#8aa9d6' : '#7c98c6';   // azul pastel apagado
    const lkPositivo = colorScheme === 'dark' ? '#86b8a6' : '#6f9d8b'; // verde pastel
    const lkNegativo = colorScheme === 'dark' ? '#d29ba0' : '#c98b91'; // rojo pastel apagado
    const lkAmber = colorScheme === 'dark' ? '#cdb487' : '#b89a63';    // ámbar pastel apagado
    const lkTitle = colorScheme === 'dark' ? '#e2e8f0' : '#1e293b';
    const lkSub = colorScheme === 'dark' ? '#94a3b8' : '#64748b';
    const lkCardBg = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
    const lkCardBorder = `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`;

    switch (dashboardTab) {
      case 0: // Simulacros
        return (
          <Stack gap="sm">
            {/* Estadísticas principales */}
            <SimpleGrid cols={4} spacing="xs" mb="sm">
              {[
                {
                  label: 'Completados',
                  valor: historial?.totalCompletados?.toString() || '0',
                  color: lkTitle
                },
                {
                  label: 'Promedio',
                  valor: historial?.promedioGeneral ? `${Math.round(historial.promedioGeneral)}%` : '0%',
                  color: lkAccent
                },
                {
                  label: 'Mejor resultado',
                  valor: historial?.mejorResultado ? `${Math.round(historial.mejorResultado)}%` : '0%',
                  color: lkTitle
                },
                {
                  label: 'Tiempo total',
                  valor: historial?.tiempoTotalMinutos ? dashboardService.formatStudyTime(historial.tiempoTotalMinutos) : '0h',
                  color: lkTitle
                },
              ].map((stat, index) => (
                <Box key={index} style={{
                  padding: '10px 12px',
                  backgroundColor: lkCardBg,
                  borderRadius: '8px',
                  textAlign: 'left',
                  border: lkCardBorder,
                }}>
                  <Text size="lg" fw={700} style={{ color: stat.color }}>
                    {stat.valor}
                  </Text>
                  <Text size="xs" style={{ color: lkSub }}>
                    {stat.label}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Historial de exámenes */}
            <Box>
              
              {historial && historial.examenes && historial.examenes.length > 0 ? (
                <>
                <Stack gap="xs">
                  {historial.examenes.slice(0, 8).map((examen, index) => {
                    const puntaje = examen.puntuacion || examen.puntajeFinal || 0;
                    // El backend envía "tipo" como string ("Examen Rápido", "Simulacro ENARM", "Examen Filtrado")
                    const tipo = (examen.tipo || examen.tipoExamen || '').toLowerCase();
                    const esRapido = tipo.includes('rápido') || tipo.includes('rapido');
                    const esENARM = tipo.includes('enarm') || tipo.includes('simulacro');
                    const IconComponent = esRapido ? IconRun : esENARM ? IconTrophy : IconSettings;
                    const iconColor = esRapido ? lkAccent : esENARM ? lkNegativo : lkAmber;
                    
                    return (
                    <Box key={index} style={{
                      padding: '10px 12px',
                      backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '8px',
                      border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                      }}
                      onClick={() => {
                        navigate(`/estudiante/resultados/${examen.intentoId}`);
                      }}
                    >
                      <Group justify="space-between" align="center" wrap="nowrap">
                        {/* Icono y título */}
                        <Group gap="xs" style={{ flex: 1, minWidth: 0 }} align="flex-start">
                          <Box
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              backgroundColor: colorScheme === 'dark'
                                ? `${iconColor}20`
                                : `${iconColor}10`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <IconComponent size={18} style={{ color: iconColor }} />
                          </Box>

                          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                            <Text size="xs" fw={600} ta="left" style={{
                              color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {examen.nombreExamen}
                            </Text>
                            <Group gap="sm">
                              <Text size="xs" ta="left" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8', fontSize: '10px' }}>
                                {examen.fechaFormatted || (examen.fecha ? dashboardService.formatDate(examen.fecha) : 'N/A')}
                              </Text>
                              <Text size="xs" ta="left" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8', fontSize: '10px' }}>
                                {examen.duracionFormatted || (examen.duracionMinutos ? dashboardService.formatStudyTime(examen.duracionMinutos) : 'N/A')}
                              </Text>
                              <Text size="xs" ta="left" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8', fontSize: '10px' }}>
                                {examen.descripcionPreguntas || `${examen.preguntasCorrectas || 0}/${examen.totalPreguntas || 0} preguntas`}
                              </Text>
                            </Group>
                          </Stack>
                        </Group>

                        {/* Porcentaje */}
                        <Text
                          size="sm"
                          fw={700}
                          ta="right"
                          style={{
                            color: puntaje >= 70 ? lkPositivo : puntaje >= 50 ? lkAmber : lkNegativo,
                            flexShrink: 0,
                          }}
                        >
                          {Math.round(puntaje)}%
                        </Text>
                      </Group>
                    </Box>
                  )})}
                </Stack>
                
                {/* Enlace para ver más si hay más de 5 */}
                {historial.examenes.length > 5 && (
                  <Box style={{ textAlign: 'left', marginTop: '16px' }}>
                    <Box
                      onClick={() => {
                        navigate('/estudiante/historial-completo');
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        backgroundColor: lkCardBg,
                        border: lkCardBorder,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = lkCardBg;
                      }}
                    >
                      <Text
                        size="sm"
                        fw={600}
                        style={{
                          color: lkAccent,
                        }}
                      >
                        Ver historial completo
                      </Text>
                      <IconChevronRight size={16} style={{ color: lkAccent }} />
                    </Box>
                  </Box>
                )}
                </>
              ) : (
                <Center style={{ padding: '40px' }}>
                  <Text size="sm" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}>
                    {loading ? 'Cargando historial...' : 'No hay exámenes en el historial'}
                  </Text>
                </Center>
              )}
            </Box>
          </Stack>
        );

      case 1: // Progreso
        return (
          <Stack gap="lg">
            {/* Sección: Tiempo de Estudio */}
            <Box>
              <Text size="sm" fw={600} mb="sm" ta="left" style={{
                color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                fontFamily: 'Space Grotesk, Inter, sans-serif',
              }}>
                Tiempo de Estudio
              </Text>
              <SimpleGrid cols={4} spacing="sm">
                {[
                  {
                    label: 'Total',
                    valor: analytics && analytics.tiempoTotalMinutos !== undefined ? dashboardService.formatStudyTime(analytics.tiempoTotalMinutos) : '42m',
                    color: lkAccent,
                    icon: <IconClock size={15} />
                  },
                  {
                    label: 'Esta semana',
                    valor: analytics && analytics.tiempoSemanalMinutos !== undefined ? dashboardService.formatStudyTime(analytics.tiempoSemanalMinutos) : '16m',
                    color: lkTitle,
                    icon: <IconCalendar size={15} />
                  },
                  {
                    label: 'Promedio diario',
                    valor: analytics && analytics.tiempoDiarioPromedioMinutos !== undefined ? dashboardService.formatStudyTime(analytics.tiempoDiarioPromedioMinutos) : '7m',
                    color: lkTitle,
                    icon: <IconChartBar size={15} />
                  },
                  {
                    label: 'Racha actual',
                    valor: analytics && analytics.rachaEstudio !== undefined ? `${analytics.rachaEstudio}` : '3',
                    suffix: ' días',
                    color: lkTitle,
                    icon: <IconFlame size={15} />
                  },
                ].map((item, index) => (
                  <Box key={index} style={{
                    padding: '16px 12px',
                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '12px',
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}>
                    <Group gap={6} align="center" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}>
                      {item.icon}
                      <Text size="xs" fw={500} style={{ textAlign: 'left' }}>
                        {item.label}
                      </Text>
                    </Group>
                    <Box>
                      <Text size="xl" fw={700} style={{ 
                        color: item.color,
                        lineHeight: 1,
                      }}>
                        {item.valor}
                      </Text>
                      {item.suffix && (
                        <Text size="xs" component="span" style={{ 
                          color: colorScheme === 'dark' ? '#64748b' : '#94a3b8',
                          marginLeft: '2px'
                        }}>
                          {item.suffix}
                        </Text>
                      )}
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Sección: Ranking */}
            <Box>
              <Text size="sm" fw={600} mb="sm" ta="left" style={{
                color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                fontFamily: 'Space Grotesk, Inter, sans-serif',
              }}>
                Tu Posición en el Ranking
              </Text>
              <SimpleGrid cols={3} spacing="sm">
                {/* Card principal con podio visual */}
                <Box style={{
                  padding: '20px',
                  backgroundColor: lkCardBg,
                  borderRadius: '12px',
                  border: lkCardBorder,
                  minHeight: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <Text size="xs" fw={500} ta="left" mb="md" style={{ color: lkSub }}>
                    Tu posición
                  </Text>
                  <Group align="baseline" gap="xs" mb="auto">
                    <Text size="3rem" fw={700} style={{ color: lkTitle, lineHeight: 1 }}>
                      #{analytics && analytics.posicionRanking ? analytics.posicionRanking : '7'}
                    </Text>
                  </Group>
                  {/* Podio visual mejorado */}
                  <Group justify="flex-start" align="flex-end" gap="xs" mt="md">
                    {/* Segundo lugar */}
                    <Box style={{
                      width: '32px',
                      height: '40px',
                      backgroundColor: analytics && analytics.posicionRanking === 2 ? '#c0c0c0' : '#cbd5e1',
                      borderRadius: '6px 6px 0 0',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      paddingBottom: '4px',
                      fontWeight: 700,
                      fontSize: '12px',
                      color: '#475569',
                      boxShadow: analytics && analytics.posicionRanking === 2 ? '0 4px 8px rgba(192, 192, 192, 0.3)' : 'none',
                    }}>
                      2
                    </Box>
                    {/* Primer lugar */}
                    <Box style={{
                      width: '32px',
                      height: '56px',
                      backgroundColor: analytics && analytics.posicionRanking === 1 ? '#fbbf24' : '#cbd5e1',
                      borderRadius: '6px 6px 0 0',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      paddingBottom: '4px',
                      fontWeight: 700,
                      fontSize: '12px',
                      color: analytics && analytics.posicionRanking === 1 ? '#92400e' : '#475569',
                      boxShadow: analytics && analytics.posicionRanking === 1 ? '0 4px 12px rgba(251, 191, 36, 0.4)' : 'none',
                    }}>
                      1
                    </Box>
                    {/* Tercer lugar */}
                    <Box style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: analytics && analytics.posicionRanking === 3 ? '#cd7f32' : '#cbd5e1',
                      borderRadius: '6px 6px 0 0',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      paddingBottom: '4px',
                      fontWeight: 700,
                      fontSize: '12px',
                      color: '#475569',
                      boxShadow: analytics && analytics.posicionRanking === 3 ? '0 4px 8px rgba(205, 127, 50, 0.3)' : 'none',
                    }}>
                      3
                    </Box>
                  </Group>
                </Box>

                {/* Percentil */}
                <Box style={{
                  padding: '20px',
                  backgroundColor: lkCardBg,
                  borderRadius: '12px',
                  border: lkCardBorder,
                  minHeight: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}>
                  <Group justify="space-between" align="flex-start" mb="md">
                    <Text size="xs" fw={500} ta="left" style={{ color: lkSub }}>
                      Percentil
                    </Text>
                    <Tooltip
                      label={
                        <Box style={{ maxWidth: '250px' }}>
                          <Text size="xs" fw={600} mb={4}>¿Qué es el Percentil?</Text>
                          <Text size="xs">
                            El percentil indica tu posición relativa respecto a todos los estudiantes. Un percentil más alto significa mejor desempeño.
                          </Text>
                        </Box>
                      }
                      multiline
                      withArrow
                      position="bottom-end"
                      styles={{
                        tooltip: {
                          backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                          color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      <ActionIcon
                        variant="transparent"
                        size="xs"
                        style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                      >
                        <IconInfoCircle size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Text size="2.5rem" fw={700} style={{ color: lkAccent, lineHeight: 1 }}>
                      {(() => {
                        const pos = analytics?.posicionRanking || 7;
                        const total = analytics?.totalUsuariosRanking || 100;
                        const percentil = Math.round(((total - pos + 1) / total) * 100);
                        return `${percentil}%`;
                      })()}
                    </Text>
                    <Text size="xs" mt="xs" style={{ color: lkSub }}>
                      Superas al {(() => {
                        const pos = analytics?.posicionRanking || 7;
                        const total = analytics?.totalUsuariosRanking || 100;
                        const percentil = Math.round(((total - pos + 1) / total) * 100);
                        return percentil;
                      })()}% de los estudiantes
                    </Text>
                  </Box>
                </Box>

                {/* Tendencia mejorada */}
                <Box style={{
                  padding: '20px',
                  backgroundColor: lkCardBg,
                  borderRadius: '12px',
                  border: lkCardBorder,
                  minHeight: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}>
                  <Group justify="space-between" align="flex-start" mb="md">
                    <Text size="xs" fw={500} ta="left" style={{ color: lkSub }}>
                      Tendencia
                    </Text>
                    <Tooltip
                      label={
                        <Box style={{ maxWidth: '250px' }}>
                          <Text size="xs" fw={600} mb={4}>¿Qué es la Tendencia?</Text>
                          <Text size="xs">
                            La tendencia muestra cómo ha evolucionado tu posición en el ranking durante las últimas semanas.
                          </Text>
                        </Box>
                      }
                      multiline
                      withArrow
                      position="bottom-end"
                      styles={{
                        tooltip: {
                          backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                          color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      <ActionIcon
                        variant="transparent"
                        size="xs"
                        style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                      >
                        <IconInfoCircle size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Group align="center" gap="sm" mb="xs">
                      <Text size="2.5rem" fw={700} style={{
                        color: analytics?.tendenciaProgreso === 'SUBIENDO' ? lkPositivo :
                               analytics?.tendenciaProgreso === 'BAJANDO' ? lkNegativo : lkSub,
                        lineHeight: 1,
                      }}>
                        {analytics?.tendenciaProgreso === 'SUBIENDO' ? '↑' :
                         analytics?.tendenciaProgreso === 'BAJANDO' ? '↓' : '—'}
                      </Text>
                      <Text size="lg" fw={600} style={{
                        color: analytics?.tendenciaProgreso === 'SUBIENDO' ? lkPositivo :
                               analytics?.tendenciaProgreso === 'BAJANDO' ? lkNegativo : lkSub
                      }}>
                        {analytics?.tendenciaProgreso === 'SUBIENDO' ? 'Subiendo' :
                         analytics?.tendenciaProgreso === 'BAJANDO' ? 'Bajando' : 'Estable'}
                      </Text>
                    </Group>
                    <Text size="xs" style={{ color: lkSub }}>
                      {analytics?.tendenciaProgreso === 'SUBIENDO' ? '¡Sigue así! Tu posición está mejorando' : 
                       analytics?.tendenciaProgreso === 'BAJANDO' ? 'Necesitas más práctica' : 'Tu posición se mantiene constante'}
                    </Text>
                  </Box>
                </Box>
              </SimpleGrid>
            </Box>
          </Stack>
        );

      case 2: { // Comparativa
        const cardBg = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
        const cardBorder = `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`;
        const titleColor = colorScheme === 'dark' ? '#e2e8f0' : '#1e293b';
        const subColor = colorScheme === 'dark' ? '#94a3b8' : '#64748b';
        const emptyBar = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';
        const neutralBar = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)';
        const accent = colorScheme === 'dark' ? '#8aa9d6' : '#7c98c6';   // azul pastel apagado para "tú"
        const positivo = colorScheme === 'dark' ? '#86b8a6' : '#6f9d8b'; // verde pastel para diferencias positivas

        if (!comparativa) {
          return (
            <Center style={{ minHeight: '220px' }}>
              <Stack align="center" gap="sm">
                <Loader />
                <Text size="sm" c="dimmed">Cargando comparativa...</Text>
              </Stack>
            </Center>
          );
        }

        const c = comparativa;
        const maxUsuarios = Math.max(1, ...c.distribucion.map((b) => b.usuarios));
        const sinDatosSemanales = c.historicoSemanal.every((s) => s.examenes === 0);
        // "Top X%" = el tramo del ranking en el que estás; complementario al percentil
        // (percentil = % de usuarios que superas) para que ambos sean consistentes.
        const topPct = Math.min(100, Math.max(1, 100 - Math.round(c.percentil)));

        // Referencia para la comparación principal: prioridad
        // (1) por especialidad si hay -> (2) global del ENARM si hay -> (3) promedio de plataforma.
        const tieneRefEsp = typeof c.enarmReferenciaEspecialidad === 'number' && c.enarmReferenciaEspecialidad !== null;
        const tieneRefGlobal = typeof c.enarmReferencia === 'number' && c.enarmReferencia !== null;
        const referencia = tieneRefEsp
          ? (c.enarmReferenciaEspecialidad as number)
          : (tieneRefGlobal ? (c.enarmReferencia as number) : c.promedioNacional);
        const refAnio = tieneRefEsp ? c.enarmReferenciaEspecialidadAnio : (tieneRefGlobal ? c.enarmReferenciaAnio : null);
        const refLabel = tieneRefEsp
          ? `Referencia ENARM ${c.especialidad || ''} ${refAnio ?? ''}`.replace(/\s+/g, ' ').trim()
          : (tieneRefGlobal ? `Referencia ENARM ${refAnio ?? ''}`.trim() : 'Promedio de la plataforma');
        const refFuente = tieneRefEsp ? c.enarmReferenciaEspecialidadFuente : (tieneRefGlobal ? c.enarmReferenciaFuente : null);
        const difRef = Math.round((c.miPromedio - referencia) * 10) / 10;
        const porEncimaRef = difRef >= 0;

        // Opciones del selector: catálogo de especialidades activas.
        const especialidadOptions = [
          { value: '__perfil__', label: c.especialidad ? `Mi especialidad (${c.especialidad})` : 'Mi especialidad' },
          ...especialidadesCatalogo.map((e) => ({ value: e.nombre, label: e.nombre })),
        ];
        const selectValue = comparativaEspecialidad ?? '__perfil__';

        return (
          <Stack gap="lg" style={{ padding: '20px' }}>
            <Group justify="space-between" align="center" wrap="wrap">
              <Text size="lg" fw={600} ta="left" style={{
                color: titleColor,
                fontFamily: 'Space Grotesk, Inter, sans-serif',
              }}>
                Comparativa de Rendimiento
              </Text>
              <Select
                size="sm"
                placeholder="Elige especialidad"
                data={especialidadOptions}
                value={selectValue}
                onChange={(v) => setComparativaEspecialidad(v === '__perfil__' || !v ? null : v)}
                searchable
                style={{ minWidth: 260 }}
              />
            </Group>

            {/* 1) Tú vs. referencia ENARM (o promedio de la plataforma si no está configurada) */}
            <Box style={{ padding: '20px', backgroundColor: cardBg, borderRadius: '12px', border: cardBorder }}>
              <Text fw={600} size="sm" mb="md" style={{ color: titleColor }}>Tú vs. {refLabel}</Text>
              <Stack gap="md">
                <Box>
                  <Group justify="space-between" mb={6} wrap="nowrap">
                    <Text size="xs" c="dimmed">Tu promedio</Text>
                    <Text size="sm" fw={700} style={{ color: titleColor }}>{c.miPromedio.toFixed(1)}%</Text>
                  </Group>
                  <Progress value={c.miPromedio} color={accent} radius="xl" size="sm" />
                </Box>
                <Box>
                  <Group justify="space-between" mb={6} wrap="nowrap">
                    <Text size="xs" c="dimmed">{refLabel}</Text>
                    <Text size="sm" fw={700} style={{ color: subColor }}>{referencia.toFixed(1)}%</Text>
                  </Group>
                  <Progress value={referencia} color="gray" radius="xl" size="sm" />
                </Box>
              </Stack>
              <Group gap={6} mt="md" wrap="nowrap" align="center">
                {porEncimaRef ? <IconTrendingUp size={14} color={positivo} /> : <IconTrendingDown size={14} color={subColor} />}
                <Text size="xs" style={{ color: porEncimaRef ? positivo : subColor }}>
                  {porEncimaRef ? '+' : ''}{difRef.toFixed(1)} pts {porEncimaRef ? 'sobre' : 'bajo'} la referencia
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                <Box>
                  <Text size="xs" c="dimmed" mb={4}>Tu promedio</Text>
                  <Text fw={700} style={{ fontSize: 30, color: '#0ea5e9' }}>{c.miPromedio.toFixed(1)}%</Text>
                  <Progress value={c.miPromedio} color="blue" mt={8} radius="xl" />
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" mb={4}>{refLabel}</Text>
                  <Text fw={700} style={{ fontSize: 30, color: subColor }}>{referencia.toFixed(1)}%</Text>
                  <Progress value={referencia} color="gray" mt={8} radius="xl" />
                </Box>
              </SimpleGrid>
              {(tieneRefEsp || tieneRefGlobal) ? (
                <Text size="xs" c="dimmed" mt="md" style={{ lineHeight: 1.5 }}>
                  {refFuente ? `Fuente: ${refFuente}. ` : ''}
                  {tieneRefEsp
                    ? 'Promedio ENARM específico de esta especialidad.'
                    : 'Promedio ENARM global; aún no hay referencia específica para esta especialidad.'}
                </Text>
              ) : (
                <Text size="xs" c="dimmed" mt="md" style={{ lineHeight: 1.5 }}>
                  El administrador aún no ha configurado una referencia ENARM; se muestra el
                  promedio de los usuarios de la plataforma.
                </Text>
              )}
            </Box>

            {/* 2) Rendimiento por semana (serie propia) */}
            <Box style={{ padding: '20px', backgroundColor: cardBg, borderRadius: '12px', border: cardBorder }}>
              <Text fw={600} size="sm" style={{ color: titleColor }}>Tu rendimiento por semana</Text>
              <Text size="xs" c="dimmed" mb="lg">Promedio de aciertos en las últimas 6 semanas</Text>
              {sinDatosSemanales ? (
                <Text size="sm" c="dimmed" ta="left" py="md">
                  Aún no hay exámenes finalizados en este periodo
                </Text>
              ) : (
                <Group align="flex-end" gap="md" wrap="nowrap" style={{ height: '160px' }}>
                  {c.historicoSemanal.map((s, i) => (
                    <Stack key={i} gap={4} align="center" style={{ flex: 1, height: '100%', justifyContent: 'flex-end' }}>
                      <Text size="xs" fw={600} style={{ color: subColor }}>
                        {s.examenes > 0 ? `${s.promedio.toFixed(0)}%` : '—'}
                      </Text>
                      <Box style={{
                        width: '60%',
                        height: `${s.examenes > 0 ? Math.max((s.promedio / 100) * 120, 4) : 2}px`,
                        borderRadius: '4px',
                        backgroundColor: s.examenes > 0 ? accent : emptyBar,
                        transition: 'height 0.3s ease',
                      }} />
                      <Text size="xs" c="dimmed">{s.etiqueta}</Text>
                    </Stack>
                  ))}
                </Group>
              )}
            </Box>

            {/* 3) Comparativa con otros usuarios */}
            <Box style={{ padding: '20px', backgroundColor: cardBg, borderRadius: '12px', border: cardBorder }}>
              <Text fw={600} size="sm" mb="md" style={{ color: titleColor }}>Cómo te comparas con otros</Text>
              <Stack gap="md" align="flex-start" mb="xl">
                <RingProgress
                  size={104}
                  thickness={8}
                  roundCaps
                  sections={[{ value: c.percentil, color: accent }]}
                  label={
                    <Text fw={700} ta="center" style={{ fontSize: 22, color: accent }}>{Math.round(c.percentil)}</Text>
                  }
                />
                <Stack gap={6} align="flex-start">
                  <Group gap={6} wrap="nowrap" align="center">
                    <Text size="sm" fw={700} ta="left" style={{ color: titleColor }}>
                      Top {topPct}% <Text span size="xs" fw={400} c="dimmed">(percentil {Math.round(c.percentil)})</Text>
                    </Text>
                    <Tooltip
                      label={
                        <Box style={{ maxWidth: '250px' }}>
                          <Text size="xs" fw={600} mb={4}>¿Qué es el percentil?</Text>
                          <Text size="xs">
                            El percentil {Math.round(c.percentil)} significa que superas al {Math.round(c.percentil)}% de los usuarios. Cuanto más alto, mejor tu posición.
                          </Text>
                        </Box>
                      }
                      multiline
                      withArrow
                      position="bottom-start"
                      styles={{
                        tooltip: {
                          backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                          color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      <ActionIcon
                        variant="transparent"
                        size="xs"
                        style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                      >
                        <IconInfoCircle size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Text size="sm" ta="left" style={{ color: subColor }}>
                    Posición <Text span fw={700} style={{ color: titleColor }}>#{c.posicion}</Text> de <Text span fw={700} style={{ color: titleColor }}>{c.totalUsuarios}</Text> usuarios
                  </Text>
                  <Text size="sm" ta="left" style={{ color: subColor }}>
                    Mejor promedio de la plataforma: <Text span fw={700} style={{ color: titleColor }}>{c.mejorPromedioNacional.toFixed(1)}%</Text>
                  </Text>
                </Stack>
              </Stack>

              <Text size="xs" c="dimmed" mb="sm">Distribución de usuarios por rango de promedio</Text>
              <Group align="flex-end" gap="md" wrap="nowrap" style={{ height: '140px' }}>
                {c.distribucion.map((b, i) => (
                  <Stack key={i} gap={4} align="center" style={{ flex: 1, height: '100%', justifyContent: 'flex-end' }}>
                    {b.contieneAlUsuario && (
                      <Text size="9px" fw={700} style={{ color: accent }}>Tú</Text>
                    )}
                    <Text size="xs" fw={600} style={{ color: b.contieneAlUsuario ? accent : subColor }}>{b.usuarios}</Text>
                    <Box style={{
                      width: '60%',
                      height: `${Math.max((b.usuarios / maxUsuarios) * 80, 4)}px`,
                      borderRadius: '4px',
                      backgroundColor: b.contieneAlUsuario ? accent : neutralBar,
                      transition: 'height 0.3s ease',
                    }} />
                    <Text size="9px" c="dimmed">{b.rango}</Text>
                  </Stack>
                ))}
              </Group>
            </Box>
          </Stack>
        );
      }

      case 3: // Materias
        return (
          <Stack gap="md">
            <Text size="sm" fw={600} ta="left" style={{
              color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
              fontFamily: 'Space Grotesk, Inter, sans-serif',
            }}>
              Progreso por Materias
            </Text>

            {/* Lista vertical de materias */}
            <Stack gap="xs">
              {progreso && progreso.progresoMaterias && progreso.progresoMaterias.length > 0 ? (
                progreso.progresoMaterias.map((especialidad, index) => {
                  const expandido = expandedItems[index] || false;
                  const porcentaje = Math.round(especialidad.porcentaje || 0);
                  const temas = especialidad.temas || [];
                  const colorFinal = getEspecialidadColor(especialidad.nombre, especialidad.colorBarra);

                  return (
                    <Box key={index}>
                      <Box
                        onClick={() => temas.length > 0 && toggleExpanded(index)}
                        style={{
                          cursor: temas.length > 0 ? 'pointer' : 'default',
                          padding: '14px 16px',
                          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                          borderRadius: '8px',
                          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (temas.length > 0) {
                            e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                        }}
                      >
                        <Group justify="space-between" mb="sm" wrap="nowrap">
                          <Group gap="sm" style={{ flex: 1 }}>
                            {(() => {
                              const IconComponent = getEspecialidadIcon(especialidad.nombre);
                              return (
                                <ThemeIcon
                                  size="md"
                                  radius="sm"
                                  style={{
                                    backgroundColor: `${colorFinal}20`,
                                    color: colorFinal,
                                    flexShrink: 0,
                                  }}
                                >
                                  <IconComponent size={18} stroke={2} />
                                </ThemeIcon>
                              );
                            })()}
                            <Text size="sm" fw={600} ta="left" style={{
                              color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                              flex: 1,
                            }}>
                              {especialidad.nombre}
                            </Text>
                            {temas.length > 0 && (
                              <Box style={{
                                transform: expandido ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                color: colorScheme === 'dark' ? '#64748b' : '#94a3b8',
                                fontSize: '11px',
                                flexShrink: 0,
                              }}>
                                ▼
                              </Box>
                            )}
                          </Group>
                          <Text size="lg" fw={700} style={{ 
                            color: colorFinal,
                            minWidth: '50px',
                            textAlign: 'right',
                            flexShrink: 0,
                          }}>
                            {porcentaje}%
                          </Text>
                        </Group>

                        <Box style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}>
                          <Box style={{
                            width: `${porcentaje}%`,
                            height: '100%',
                            backgroundColor: colorFinal,
                            borderRadius: '4px',
                            transition: 'width 0.4s ease',
                          }} />
                        </Box>
                      </Box>

                      {expandido && temas.length > 0 && (
                        <Box style={{
                          marginTop: '8px',
                          marginLeft: '24px',
                          padding: '12px',
                          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)',
                          borderRadius: '8px',
                          borderLeft: `3px solid ${colorFinal}`,
                        }}>
                          <Stack gap="sm">
                            {temas.map((tema, subIndex) => {
                              const temaPorcentaje = Math.round(tema.porcentaje || 0);
                              const temaColor = temaPorcentaje >= 80 ? '#10b981' : temaPorcentaje >= 60 ? '#f59e0b' : '#ef4444';
                              return (
                                <Box key={subIndex}>
                                  <Group justify="space-between" mb={6}>
                                    <Text size="xs" fw={400} ta="left" style={{
                                      color: colorScheme === 'dark' ? '#cbd5e1' : '#475569',
                                    }}>
                                      {tema.nombre}
                                    </Text>
                                    <Text size="xs" fw={600} style={{
                                      color: temaColor,
                                      minWidth: '40px',
                                      textAlign: 'right',
                                    }}>
                                      {temaPorcentaje}%
                                    </Text>
                                  </Group>
                                  <Box style={{
                                    width: '100%',
                                    height: '5px',
                                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
                                    borderRadius: '3px',
                                    overflow: 'hidden',
                                  }}>
                                    <Box style={{
                                      width: `${temaPorcentaje}%`,
                                      height: '100%',
                                      backgroundColor: temaColor,
                                      borderRadius: '3px',
                                      transition: 'width 0.4s ease',
                                    }} />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Center style={{ padding: '40px 20px' }}>
                  <Stack align="center" gap="xs">
                    <Text size="sm" fw={500} style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}>
                      {loading ? 'Cargando progreso...' : 'No hay datos de materias aún'}
                    </Text>
                    {!loading && (
                      <Text size="xs" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8' }}>
                        Completa algunos exámenes para ver tu progreso
                      </Text>
                    )}
                  </Stack>
                </Center>
              )}
            </Stack>
          </Stack>
        );

      default:
        return null;
    }
  };

  // Generar datos de performance usando datos reales del backend
  const getPerformanceData = () => {
    if (!dashboardStats) {
      // Datos por defecto si no hay dashboard stats
      return {
        overall: {
          score: 0,
          percentile: 0,
          improvement: 0,
          streak: 0,
          totalHours: 0,
          questionsAnswered: 0,
          accuracy: 0
        },
        subjects: [
          { name: 'Medicina Interna', score: 91, questions: 623, color: '#10b981' },
          { name: 'Cirugía', score: 78, questions: 445, color: '#3b82f6' },
          { name: 'Pediatría', score: 94, questions: 456, color: '#8b5cf6' },
          { name: 'Ginecología', score: 89, questions: 234, color: '#f59e0b' },
          { name: 'Neurología', score: 61, questions: 234, color: '#ef4444' },
          { name: 'Cardiología', score: 85, questions: 312, color: '#06b6d4' }
        ]
      };
    }

    return {
      overall: {
        score: Math.round((dashboardStats.promedioGeneral || 0) * 10) / 10,
        percentile: dashboardStats.posicionRanking && dashboardStats.totalUsuarios 
          ? Math.round(((dashboardStats.totalUsuarios - dashboardStats.posicionRanking) / dashboardStats.totalUsuarios) * 100)
          : 0,
        improvement: 0, // Este dato requiere cálculo histórico
        streak: 0, // TODO: Implementar racha
        totalHours: dashboardStats.tiempoTotalHoras || 0,
        questionsAnswered: 0, // Este dato requiere agregación de preguntas
        accuracy: Math.round((dashboardStats.promedioGeneral || 0) * 10) / 10
      },
      subjects: progreso && progreso.progresoMaterias && progreso.progresoMaterias.length > 0 
        ? progreso.progresoMaterias.slice(0, 6).map(esp => ({
            name: esp.nombre || 'Sin nombre',
            score: Math.round(esp.porcentaje || 0),
            questions: esp.preguntasRespondidas || 0,
            color: esp.colorBarra || dashboardService.getProgresoColor(esp.porcentaje || 0)
          }))
        : [
        { name: 'Medicina Interna', score: 91, questions: 623, color: '#10b981' },
        { name: 'Cirugía', score: 78, questions: 445, color: '#3b82f6' },
        { name: 'Pediatría', score: 94, questions: 456, color: '#8b5cf6' },
        { name: 'Ginecología', score: 89, questions: 234, color: '#f59e0b' },
        { name: 'Neurología', score: 61, questions: 234, color: '#ef4444' },
        { name: 'Cardiología', score: 85, questions: 312, color: '#06b6d4' }
      ]
    };
  };
  
  const performanceData = getPerformanceData();
  
  // Calcular porcentajes de tiempo por día
  const getTiempoPorDiaData = () => {
    if (!dashboardStats?.tiempoPorDiaSemanaActual) {
      return { dias: [], totalSegundos: 0, tiempoFormatted: '0h' };
    }
    
    const diasOrden = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const colores = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];
    
    const tiemposPorDia = dashboardStats.tiempoPorDiaSemanaActual;
    
    // Calcular total de segundos
    const totalSegundos = Object.values(tiemposPorDia).reduce((acc, val) => acc + val, 0);
    
    // Calcular porcentajes y crear estructura de datos
    const dias = diasOrden.map((dia, index) => {
      const segundos = tiemposPorDia[dia] || 0;
      const porcentaje = totalSegundos > 0 ? Math.round((segundos / totalSegundos) * 100) : 0;
      return {
        nombre: dia,
        porcentaje,
        color: colores[index],
        segundos
      };
    });
    
    return {
      dias,
      totalSegundos,
      tiempoFormatted: dashboardStats.tiempoSemanalFormatted || '0h'
    };
  };
  
  const tiempoPorDia = getTiempoPorDiaData();

  // Mostrar loading mientras cargan los datos
  if (loading) {
    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Center>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="md" c="dimmed">Cargando análisis de rendimiento...</Text>
          </Stack>
        </Center>
      </Box>
    );
  }

  // Mostrar error si ocurre
  if (error) {
    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Alert color="red" variant="light" title="Error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Box
        style={{
          padding: '16px 24px',
          overflow: 'hidden',
          overflowY: 'auto',
          flex: 1,
          width: '100%',
        }}
      >
        {/* Page Header */}
        <Box mb="sm" ta="left">
          <Title
            order={2}
            size="1.3rem"
            fw={600}
            ta="left"
            style={{
              color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
              fontFamily: 'Space Grotesk, Inter, sans-serif',
              marginBottom: '4px',
            }}
          >
            Estadísticas de Rendimiento
          </Title>
          <Text
            size="xs"
            ta="left"
            style={{
              color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Análisis profundo con insights accionables para tu preparación ENARM
          </Text>
        </Box>

        {/* Responsive: tablet 2 columnas, móvil 1 columna; se neutralizan las
            posiciones fijas (gridColumn/gridRow) de las tarjetas. */}
        <style>{`
          @media (max-width: 1023px) {
            .estadisticas-grid {
              grid-template-columns: 1fr !important;
              grid-template-rows: auto !important;
              grid-auto-rows: auto !important;
            }
            .estadisticas-grid > * {
              grid-area: auto !important;
              height: auto !important;
            }
          }
        `}</style>

        {/* Main Layout - Improved Grid */}
        <Box
          className="estadisticas-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'auto auto',
            gap: '16px',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {/* Card 1: Promedio General */}
          <Card
            padding="xs"
            radius="md"
            style={{
              gridColumn: '1',
              gridRow: '1',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(34, 197, 94, 0.15)'
                : '#ebe8e3',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
              boxShadow: 'none',
              minHeight: '200px',
            }}
          >
            <Box style={{ height: '100%', padding: '12px', position: 'relative' }}>
              <Group justify="space-between" align="flex-start" mb="xs">
                <Text
                  size="xs"
                  fw={600}
                  ta="left"
                  style={{
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                  }}
                >
                  Promedio General
                </Text>
                <Tooltip
                  label={
                    <Box style={{ maxWidth: '280px' }}>
                      <Text size="xs" fw={600} mb={4}>¿Qué es el Promedio General?</Text>
                      <Text size="xs" mb={8}>
                        Promedio de los porcentajes de todos tus exámenes. Cada examen cuenta por igual.
                      </Text>
                      <Text size="xs" fw={500} mb={4} style={{ color: '#3b82f6' }}>Ejemplo:</Text>
                      <Text size="xs">
                        • Examen 1: 70%{' • '}Examen 2: 90%
                        <br />
                        <strong>Promedio: (70 + 90) / 2 = 80%</strong>
                      </Text>
                    </Box>
                  }
                  multiline
                  withArrow
                  position="bottom-end"
                  styles={{
                    tooltip: {
                      backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <ActionIcon
                    variant="transparent"
                    size="xs"
                    style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                  >
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 40px)' }}>
                <RingProgress
                  size={110}
                  thickness={8}
                  sections={[
                    { value: performanceData.overall.score, color: '#22C55E' },
                    { value: 100 - performanceData.overall.score, color: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }
                  ]}
                  label={
                    <Center>
                      <Text size="xl" fw={700} style={{ color: colorScheme === 'dark' ? '#ffffff' : '#000000' }}>
                        {Math.round(performanceData.overall.score)}%
                      </Text>
                    </Center>
                  }
                />
              </Box>
            </Box>
          </Card>


          {/* Card 2: Tiempo Total */}
          <Card
            padding="lg"
            radius="md"
            style={{
              gridColumn: '2',
              gridRow: '1',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack gap="xs" h="100%">
              <Group justify="space-between" align="flex-start">
                <Text size="xs" fw={600} ta="left" style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26', fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                  Tiempo Total
                </Text>
                <Tooltip
                  label={
                    <Box style={{ maxWidth: '250px' }}>
                      <Text size="xs" fw={600} mb={4}>¿Cómo se calcula el Tiempo Total?</Text>
                      <Text size="xs">
                        Este tiempo representa la suma total de todas las horas que has dedicado a estudiar en la plataforma durante la semana actual. 
                        El gráfico muestra la distribución por día de la semana.
                      </Text>
                    </Box>
                  }
                  multiline
                  withArrow
                  position="bottom-end"
                  styles={{
                    tooltip: {
                      backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <ActionIcon
                    variant="transparent"
                    size="xs"
                    style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                  >
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flex: 1 }}>
                <Box style={{ position: 'relative', width: '100px', height: '100px', flexShrink: 0 }}>
                    <svg width="100" height="100" viewBox="0 0 100 100">
                      <defs>
                        <mask id="centerMask">
                          <rect width="100" height="100" fill="white"/>
                          <circle cx="50" cy="50" r="30" fill="black"/>
                        </mask>
                      </defs>

                      {/* Círculo base gris */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke={colorScheme === 'dark' ? '#374151' : '#e5e7eb'}
                        strokeWidth="8"
                        mask="url(#centerMask)"
                      />

                      {/* Segmentos de colores dinámicos */}
                      {(() => {
                        const circunferencia = 2 * Math.PI * 40; // 2πr donde r=40
                        let offsetAcumulado = 0;
                        
                        return tiempoPorDia.dias.map((dia, index) => {
                          if (dia.porcentaje === 0) return null;
                          
                          const longitudSegmento = (dia.porcentaje / 100) * circunferencia;
                          const dashArray = `${longitudSegmento} ${circunferencia - longitudSegmento}`;
                          const offset = -offsetAcumulado;
                          offsetAcumulado += longitudSegmento;
                          
                          return (
                            <circle
                              key={index}
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke={dia.color}
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={dashArray}
                              strokeDashoffset={offset}
                              transform="rotate(-90 50 50)"
                              mask="url(#centerMask)"
                            />
                          );
                        });
                      })()}
                    </svg>

                    {/* Texto central */}
                    <Box
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center'
                      }}
                    >
                      <Text ta="center" fw={700} size="lg" style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b', fontFamily: 'Space Grotesk' }}>
                        {tiempoPorDia.tiempoFormatted}
                      </Text>
                    </Box>
                  </Box>

                <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', flex: 1 }}>
                    <Stack gap={4}>
                      {tiempoPorDia.dias.slice(0, 4).map((dia, index) => (
                        <Group key={index} gap={4} align="center" wrap="nowrap">
                          <Box style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: dia.color, flexShrink: 0 }} />
                          <Text size="xs" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b', fontSize: '9px', whiteSpace: 'nowrap' }}>
                            {dia.nombre} {dia.porcentaje}%
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                    <Stack gap={4}>
                      {tiempoPorDia.dias.slice(4, 7).map((dia, index) => (
                        <Group key={index} gap={4} align="center" wrap="nowrap">
                          <Box style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: dia.color, flexShrink: 0 }} />
                          <Text size="xs" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b', fontSize: '9px', whiteSpace: 'nowrap' }}>
                            {dia.nombre} {dia.porcentaje}%
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                </Box>
            </Stack>
          </Card>

          {/* Card 3: Precisión */}
          <Card
            padding="lg"
            radius="md"
            style={{
              gridColumn: '3',
              gridRow: '1',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack gap="xs" h="100%">
              <Group justify="space-between" align="flex-start">
                <Text size="xs" fw={600} ta="left" style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26', fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                  Precisión
                </Text>
                <Group gap="xs">
                  <Tooltip
                    label={
                      <Box style={{ maxWidth: '280px' }}>
                        <Text size="xs" fw={600} mb={4}>¿Qué es la Precisión?</Text>
                        <Text size="xs" mb={8}>
                          Total de respuestas correctas dividido entre total de preguntas. Da más peso a exámenes largos.
                        </Text>
                        <Text size="xs" fw={500} mb={4} style={{ color: '#3b82f6' }}>Ejemplo:</Text>
                        <Text size="xs">
                          • Examen 1: 7/10 correctas{' • '}Examen 2: 85/100
                          <br />
                          <strong>Precisión: (7 + 85) / (10 + 100) = 83.6%</strong>
                        </Text>
                      </Box>
                    }
                    multiline
                    withArrow
                    position="bottom-end"
                    styles={{
                      tooltip: {
                        backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                        color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                        border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <ActionIcon
                      variant="transparent"
                      size="xs"
                      style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                    >
                      <IconInfoCircle size={14} />
                    </ActionIcon>
                  </Tooltip>
                  
                  <Tooltip
                    label={
                      <Box style={{ maxWidth: '220px' }}>
                        <Text size="xs" fw={600} mb={8}>Leyenda de colores</Text>
                        <Stack gap="xs">
                          <Group gap="xs">
                            <Box style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#fef08a', border: '1px solid #eab308' }} />
                            <Text size="xs">Centro: 90-100%</Text>
                          </Group>
                          <Group gap="xs">
                            <Box style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#fbbf24' }} />
                            <Text size="xs">Amarillo: 75-90%</Text>
                          </Group>
                          <Group gap="xs">
                            <Box style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                            <Text size="xs">Rojo: 50-75%</Text>
                          </Group>
                          <Group gap="xs">
                            <Box style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#60a5fa' }} />
                            <Text size="xs">Azul: 25-50%</Text>
                          </Group>
                          <Group gap="xs">
                            <Box style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#1e293b' }} />
                            <Text size="xs">Negro: 0-25%</Text>
                          </Group>
                        </Stack>
                      </Box>
                    }
                    multiline
                    withArrow
                    position="bottom-end"
                    styles={{
                      tooltip: {
                        backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                        color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                        border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <ActionIcon
                      variant="transparent"
                      size="xs"
                      style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                    >
                      <IconPalette size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, position: 'relative' }}>
                {/* Diana SVG tipo Archery */}
                <svg width="110" height="110" viewBox="-10 -10 120 120" style={{ overflow: 'visible' }}>
                  {/* Círculo blanco exterior */}
                  <circle cx="50" cy="50" r="48" fill="#ffffff" stroke={colorScheme === 'dark' ? '#475569' : '#cbd5e1'} strokeWidth="1" />
                  {/* Negro - exterior */}
                  <circle cx="50" cy="50" r="45" fill="#1e293b" />
                  {/* Azul - 4to anillo */}
                  <circle cx="50" cy="50" r="36" fill="#60a5fa" />
                  {/* Rojo - 3er anillo */}
                  <circle cx="50" cy="50" r="27" fill="#ef4444" />
                  {/* Amarillo - 2do anillo */}
                  <circle cx="50" cy="50" r="18" fill="#fbbf24" />
                  {/* Centro amarillo */}
                  <circle cx="50" cy="50" r="9" fill="#fef08a" />
                  
                  {/* Flecha dinámica estilo realista */}
                  {(() => {
                    // Calcular precisión real: preguntas correctas / total de preguntas
                    let precision = 0;
                    if (historial && historial.examenes && historial.examenes.length > 0) {
                      const totalPreguntas = historial.examenes.reduce((sum, ex) => sum + (ex.totalPreguntas || 0), 0);
                      const totalCorrectas = historial.examenes.reduce((sum, ex) => sum + (ex.preguntasCorrectas || 0), 0);
                      precision = totalPreguntas > 0 ? (totalCorrectas / totalPreguntas) * 100 : 0;
                    } else {
                      precision = analytics?.promedioGeneral || performanceData.overall.score || 0;
                    }
                    // Calcular posición de la flecha basada en precisión
                    const maxRadius = 45;
                    const minRadius = 3;
                    const radius = maxRadius - (precision / 100) * (maxRadius - minRadius);
                    
                    // Ángulo hacia arriba-derecha
                    const angle = -30; // grados
                    const angleRad = (angle * Math.PI) / 180;
                    const x = 50 + radius * Math.cos(angleRad);
                    const y = 50 + radius * Math.sin(angleRad);
                    
                    // Calcular punto de origen (más allá del borde para palo más largo)
                    const startX = 50 + 70 * Math.cos(angleRad);
                    const startY = 50 + 70 * Math.sin(angleRad);
                    
                    // Calcular vectores perpendiculares para las plumas
                    const dx = x - startX;
                    const dy = y - startY;
                    const length = Math.sqrt(dx * dx + dy * dy);
                    const ux = dx / length;
                    const uy = dy / length;
                    const perpX = -uy;
                    const perpY = ux;
                    
                    return (
                      <g>
                        {/* Cuerpo de la flecha (vara) - más largo y grueso */}
                        <line
                          x1={startX}
                          y1={startY}
                          x2={x - ux * 3}
                          y2={y - uy * 3}
                          stroke="#78350f"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                        
                        {/* Pluma superior */}
                        <path
                          d={`M ${startX + ux * 3} ${startY + uy * 3} 
                              L ${startX + perpX * 5} ${startY + perpY * 5}
                              L ${startX + ux * 10 + perpX * 1} ${startY + uy * 10 + perpY * 1}
                              Z`}
                          fill="#dc2626"
                          stroke="#991b1b"
                          strokeWidth="0.5"
                          opacity="0.9"
                        />
                        
                        {/* Pluma inferior */}
                        <path
                          d={`M ${startX + ux * 3} ${startY + uy * 3} 
                              L ${startX - perpX * 5} ${startY - perpY * 5}
                              L ${startX + ux * 10 - perpX * 1} ${startY + uy * 10 - perpY * 1}
                              Z`}
                          fill="#dc2626"
                          stroke="#991b1b"
                          strokeWidth="0.5"
                          opacity="0.9"
                        />
                        
                        {/* Punta de la flecha (metálica) - sobresale del palo */}
                        <path
                          d={`M ${x} ${y}
                              L ${x - ux * 5 + perpX * 1.5} ${y - uy * 5 + perpY * 1.5}
                              L ${x - ux * 5 - perpX * 1.5} ${y - uy * 5 - perpY * 1.5}
                              Z`}
                          fill="#64748b"
                          stroke="#334155"
                          strokeWidth="0.5"
                        />
                      </g>
                    );
                  })()}
                </svg>
                
                {/* Porcentaje en la esquina inferior derecha */}
                <Text
                  size="lg"
                  fw={700}
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                  }}
                >
                  {(() => {
                    // Calcular precisión real: preguntas correctas / total de preguntas
                    if (historial && historial.examenes && historial.examenes.length > 0) {
                      const totalPreguntas = historial.examenes.reduce((sum, ex) => sum + (ex.totalPreguntas || 0), 0);
                      const totalCorrectas = historial.examenes.reduce((sum, ex) => sum + (ex.preguntasCorrectas || 0), 0);
                      const precision = totalPreguntas > 0 ? (totalCorrectas / totalPreguntas) * 100 : 0;
                      return `${Math.round(precision)}%`;
                    }
                    return `${Math.round(analytics?.promedioGeneral || performanceData.overall.score || 0)}%`;
                  })()}
                </Text>
              </Box>
            </Stack>
          </Card>

          {/* Card 4: Racha de Estudio */}
          <Card
            padding="lg"
            radius="md"
            style={{
              gridColumn: '1 / 2',
              gridRow: '2',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack gap="xs" h="100%">
              <Group justify="space-between" align="flex-start">
                <Text size="xs" fw={600} ta="left" style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26', fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                  Racha de Estudio
                </Text>
                <Group 
                  gap={2} 
                  align="center" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    console.log('Abriendo modal, collapsed actual:', collapsed);
                    // Guardar estado del sidebar principal
                    setSidebarWasOpen(!collapsed);
                    // Colapsar sidebar principal
                    console.log('Colapsando sidebar...');
                    setCollapsed(true);
                    // Cerrar sidebar del modal
                    setShowSidebar(false);
                    // Abrir modal
                    setCalendarioModalOpen(true);
                  }}
                >
                  <Text size="xs" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8', fontSize: '10px' }}>Ver historial</Text>
                  <IconChevronRight size={12} style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8' }} />
                </Group>
              </Group>
              <Box style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <Stack align="center" gap="xs">
                  <Group justify="center" gap="sm">
                    {dashboardStats && dashboardStats.diasActivosRacha ? (
                      dashboardStats.diasActivosRacha.map((activo, i) => (
                        <Stack key={i} align="center" gap="xs">
                          <IconFlame
                            size={24}
                            style={{
                              color: activo ? '#f59e0b' : '#94a3b8',
                              fill: activo ? '#f59e0b' : 'transparent'
                            }}
                          />
                          <Text
                            size="xs"
                            style={{
                              color: activo ? '#f59e0b' : '#94a3b8',
                              fontSize: '10px',
                              fontWeight: activo ? 600 : 400
                            }}
                          >
                            {['L', 'M', 'M', 'J', 'V', 'S', 'D'][i]}
                          </Text>
                        </Stack>
                      ))
                    ) : (
                      [...Array(7)].map((_, i) => (
                        <Stack key={i} align="center" gap="xs">
                          <IconFlame
                            size={24}
                            style={{
                              color: '#94a3b8',
                              fill: 'transparent'
                            }}
                          />
                          <Text size="xs" style={{ color: '#94a3b8', fontSize: '10px' }}>
                            {['L', 'M', 'M', 'J', 'V', 'S', 'D'][i]}
                          </Text>
                        </Stack>
                      ))
                    )}
                  </Group>
                </Stack>
                <Text
                  size="xs"
                  style={{
                    color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px'
                  }}
                >
                  Racha actual: {dashboardStats?.rachaActual || 0} días
                </Text>
              </Box>
            </Stack>
          </Card>

          {/* Card 5: Progreso por especialidad */}
          <Card
            padding="lg"
            radius="md"
            style={{
              gridColumn: '2 / 4',
              gridRow: '2',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <Text size="xs" fw={600} ta="left" style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26', fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                  Progreso por especialidad
                </Text>
                <Tooltip
                  label={
                    <Box style={{ maxWidth: '250px' }}>
                      <Text size="xs" fw={600} mb={4}>¿Qué es el Progreso por Especialidad?</Text>
                      <Text size="xs">
                        Muestra tu porcentaje de dominio en cada especialidad médica basado en las preguntas que has respondido correctamente.
                        Identifica tus áreas fuertes y aquellas que necesitan más práctica.
                      </Text>
                    </Box>
                  }
                  multiline
                  withArrow
                  position="bottom-end"
                  styles={{
                    tooltip: {
                      backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <ActionIcon
                    variant="transparent"
                    size="xs"
                    style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                  >
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '16px', justifyItems: 'center' }}>
                {analytics && analytics.progresoEspecialidades && analytics.progresoEspecialidades.length > 0 ? (
                  analytics.progresoEspecialidades.map((esp, index) => {
                    const porcentaje = Math.round(esp.porcentaje || 0);
                    // Color unificado por id de especialidad (mismo que Videos/Dashboard)
                    const color = colorEspecialidad(esp.especialidadId);

                    return (
                      <Stack key={index} align="center" gap="xs">
                        <RingProgress
                          size={65}
                          thickness={7}
                          roundCaps
                          sections={[{ value: porcentaje, color }]}
                          label={
                            <Stack align="center" gap={1}>
                              <Text ta="center" fw={400} size="xs" style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#000000', fontSize: '11px' }}>
                                {porcentaje}%
                              </Text>
                              {/* Icono de tendencia omitido por ahora */}
                            </Stack>
                          }
                        />
                        <Text size="xs" ta="center" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b', fontSize: '10px' }}>
                          {capitalizarEspecialidad(esp.nombre)}
                        </Text>
                      </Stack>
                    );
                  })
                ) : (
                  <Text size="sm" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b', gridColumn: '1 / -1', textAlign: 'center' }}>
                    No hay datos de especialidades aún
                  </Text>
                )}
              </Box>
            </Stack>
          </Card>

          {/* Nueva Sección: Área de Contenido Principal del Dashboard */}
          <Card
            padding="xs"
            radius="md"
            style={{
              gridColumn: '1 / -1',
              gridRow: '3',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(247, 243, 238, 0.8)',
              backdropFilter: 'blur(40px) saturate(200%)',
              WebkitBackdropFilter: 'blur(40px) saturate(200%)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '500px',
            }}
          >
            {/* Mini Navbar */}
            <Group gap="xs" mb="sm" style={{
              borderBottom: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)'}`,
              paddingBottom: '8px'
            }}>
              {['Simulacros', 'Progreso', 'Comparativa', 'Materias'].map((tab, index) => {
                const isDisabled = false;
                return (
                  <Box
                    key={tab}
                    onClick={() => !isDisabled && setDashboardTab(index)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      backgroundColor: index === dashboardTab
                        ? (colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.85)')
                        : 'transparent',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease',
                      opacity: isDisabled ? 0.4 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (index !== dashboardTab && !isDisabled) {
                        e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.04)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (index !== dashboardTab && !isDisabled) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Text
                      size="xs"
                      fw={index === dashboardTab ? 600 : 500}
                      style={{
                        color: index === dashboardTab
                          ? '#ffffff'
                          : (colorScheme === 'dark' ? '#94a3b8' : '#475569'),
                        transition: 'color 0.15s ease',
                      }}
                    >
                      {tab}
                    </Text>
                  </Box>
                );
              })}
            </Group>

            {/* Content Area */}
            <Box style={{ flex: 1, overflow: 'auto', padding: '0 16px' }}>
              {renderDashboardTabContent()}
            </Box>
          </Card>

        </Box>
      </Box>

      {/* Sticky Footer */}
      <Box
        style={{
          borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.5)'}`,
          textAlign: 'center',
          padding: '1rem',
          backgroundColor: colorScheme === 'dark'
            ? 'rgba(15, 23, 42, 0.8)'
            : 'rgba(247, 243, 238, 0.8)',
          marginTop: 'auto',
        }}
      >
        <Text
          size="xs"
          style={{
            color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          © 2024 ENARM360. Todos los derechos reservados.
        </Text>
      </Box>
      
      {/* Modal de Calendario Histórico */}
      <Modal
        opened={calendarioModalOpen}
        onClose={() => {
          console.log('Cerrando modal, restaurando sidebar:', sidebarWasOpen);
          setCalendarioModalOpen(false);
          setShowStreakAnimation(false);
          setModalJustOpened(false);
          // Restaurar sidebar principal si estaba abierto
          if (sidebarWasOpen) {
            console.log('Restaurando sidebar a abierto');
            setCollapsed(false);
          }
        }}
        withCloseButton={true}
        size="xl"
        centered
        radius="xl"
        styles={{
          header: {
            display: 'none',
          },
          body: {
            backgroundColor: colorScheme === 'dark' ? '#0f172a' : '#ffffff',
            padding: 0,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '24px',
            minHeight: '500px',
          },
          content: {
            backgroundColor: colorScheme === 'dark' ? '#0f172a' : '#ffffff',
            borderRadius: '24px',
            overflow: 'hidden',
          },
          close: {
            position: 'absolute',
            top: '12px',
            right: '12px',
            zIndex: 1001,
            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          },
        }}
      >
        {/* Animación inicial de racha */}
        {showStreakAnimation && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: colorScheme === 'dark' 
                ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              animation: 'fadeOut 1.5s ease-out forwards',
            }}
          >
            <IconFlame size={120} style={{ color: '#f59e0b', fill: '#fbbf24', marginBottom: '20px' }} />
            <Text size="80px" fw={900} style={{ color: colorScheme === 'dark' ? '#f59e0b' : '#f97316', fontFamily: 'Space Grotesk, Inter, sans-serif', lineHeight: 1 }}>
              {dashboardStats?.rachaActual || 0}
            </Text>
            <Text size="xl" fw={600} style={{ color: colorScheme === 'dark' ? '#fbbf24' : '#f59e0b', marginTop: '8px' }}>
              day streak
            </Text>
          </Box>
        )}

        <style>
          {`
            @keyframes fadeOut {
              0% {
                opacity: 1;
              }
              70% {
                opacity: 1;
              }
              100% {
                opacity: 0;
                pointer-events: none;
              }
            }
            @keyframes pulse {
              0%, 100% {
                transform: translateY(-50%) scale(1);
                opacity: 1;
              }
              50% {
                transform: translateY(-50%) scale(1.1);
                opacity: 0.8;
              }
            }
          `}
        </style>

        <Box 
          style={{ 
            display: 'flex', 
            height: '100%', 
            minHeight: '500px',
            position: 'relative', 
            overflow: 'hidden',
            opacity: showStreakAnimation ? 0 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          {/* Calendario principal */}
          <Box style={{ 
            flex: 1, 
            padding: '0', 
            overflowY: 'auto', 
            display: 'flex', 
            flexDirection: 'column', 
            position: 'relative',
            overflow: 'hidden',
          }}>
            <CalendarioHistorico 
              mes={calendarioMes}
              year={calendarioYear}
              onMesChange={(newMes, newYear) => {
                setCalendarioMes(newMes);
                setCalendarioYear(newYear);
              }}
              colorScheme={colorScheme}
              diasActivos={diasActivosCalendario}
            />
          </Box>

          {/* Sidebar de estadísticas integrado */}
          <Box
            style={{
              width: '200px',
              borderLeft: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              backgroundColor: colorScheme === 'dark' ? '#0f172a' : '#ffffff',
              padding: '16px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack gap="lg">
              <Text size="sm" fw={700} style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b' }}>
                Estadísticas
              </Text>
              
              <Stack gap="md">
                <Box>
                  <Group gap="xs" mb={4}>
                    <IconFlame size={16} style={{ color: '#f59e0b' }} />
                    <Text size="xs" c="dimmed">Racha actual</Text>
                  </Group>
                  <Text size="xl" fw={700} style={{ color: '#f59e0b' }}>
                    {dashboardStats?.rachaActual || 0} días
                  </Text>
                </Box>

                <Box>
                  <Group gap="xs" mb={4}>
                    <IconTrophy size={16} style={{ color: '#22c55e' }} />
                    <Text size="xs" c="dimmed">Mejor racha</Text>
                  </Group>
                  <Text size="xl" fw={700} style={{ color: '#22c55e' }}>
                    {dashboardStats?.mejorRacha || 0} días
                  </Text>
                </Box>

                <Box>
                  <Group gap="xs" mb={4}>
                    <IconStar size={16} style={{ color: '#3b82f6' }} />
                    <Text size="xs" c="dimmed">Total este mes</Text>
                  </Group>
                  <Text size="xl" fw={700} style={{ color: '#3b82f6' }}>
                    {diasActivosCalendario.length} días
                  </Text>
                </Box>
              </Stack>

              <Box>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.6 }}>
                  Mantén tu racha estudiando todos los días para mejorar tu preparación para el ENARM.
                </Text>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

// Componente de Calendario Histórico
interface CalendarioHistoricoProps {
  mes: number;
  year: number;
  onMesChange: (mes: number, year: number) => void;
  colorScheme: 'light' | 'dark' | 'auto';
  diasActivos: number[];
}

const CalendarioHistorico: React.FC<CalendarioHistoricoProps> = ({ mes, year, onMesChange, colorScheme, diasActivos: diasActivosProp }) => {
  const [diasActivos, setDiasActivos] = useState<number[]>(diasActivosProp || []);
  const [loading, setLoading] = useState(false);

  // Cargar días activos del mes
  useEffect(() => {
    const cargarDias = async () => {
      setLoading(true);
      try {
        const data = await dashboardService.getCalendarioMes(year, mes);
        setDiasActivos(data.diasActivos || []);
      } catch (error) {
        console.error('Error cargando calendario:', error);
      } finally {
        setLoading(false);
      }
    };
    cargarDias();
  }, [mes, year]);

  const nombresMeses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const diasEnMes = new Date(year, mes, 0).getDate();
  const primerDiaSemana = new Date(year, mes - 1, 1).getDay(); // 0=Dom, 1=Lun, ...
  
  const mesAnterior = () => {
    if (mes === 1) {
      onMesChange(12, year - 1);
    } else {
      onMesChange(mes - 1, year);
    }
  };

  const mesSiguiente = () => {
    if (mes === 12) {
      onMesChange(1, year + 1);
    } else {
      onMesChange(mes + 1, year);
    }
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Navegación de mes */}
      <Group justify="flex-start" align="center" gap="sm" style={{ 
        padding: '32px 20px', 
        borderBottom: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        backgroundColor: colorScheme === 'dark' ? '#3d3530' : '#c9bdb1',
      }}>
        <Text size="lg" fw={700} style={{ fontFamily: 'Space Grotesk, Inter, sans-serif', color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b' }}>
          {nombresMeses[mes - 1]} {year}
        </Text>
        <ActionIcon 
          onClick={mesAnterior} 
          variant="default" 
          size="md"
          style={{
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
            borderRadius: '8px',
          }}
        >
          <IconChevronLeft size={16} />
        </ActionIcon>
        <ActionIcon 
          onClick={mesSiguiente} 
          variant="default" 
          size="md"
          style={{
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
            borderRadius: '8px',
          }}
        >
          <IconChevronRight size={16} />
        </ActionIcon>
      </Group>

      {loading ? (
        <Center style={{ flex: 1 }}>
          <Loader size="md" />
        </Center>
      ) : (
        <Box style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>

          {/* Grid de días */}
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              flex: 1,
            }}
          >
            {/* Celdas con nombre de día y espacios vacíos */}
            {[...Array(primerDiaSemana)].map((_, i) => {
              const nombresDias = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              return (
                <Box
                  key={`empty-${i}`}
                  style={{
                    minHeight: '70px',
                    borderRight: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    borderBottom: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    padding: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Text 
                    size="xs" 
                    fw={500} 
                    mb="xs"
                    style={{ 
                      color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                      fontFamily: 'Space Grotesk, Inter, sans-serif'
                    }}
                  >
                    {nombresDias[i]}
                  </Text>
                </Box>
              );
            })}
            
            {/* Días del mes */}
            {[...Array(diasEnMes)].map((_, i) => {
              const dia = i + 1;
              const activo = diasActivos.includes(dia);
              const colIndex = (primerDiaSemana + i) % 7;
              const isLastCol = colIndex === 6;
              const nombresDias = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const nombreDia = nombresDias[colIndex];
              const esPrimeraFila = i < (7 - primerDiaSemana);
              
              // Verificar si es el día de hoy
              const hoy = new Date();
              const esHoy = dia === hoy.getDate() && mes === (hoy.getMonth() + 1) && year === hoy.getFullYear();
              
              return (
                <Box
                  key={dia}
                  style={{
                    minHeight: '70px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    padding: '8px',
                    borderRight: !isLastCol ? `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}` : 'none',
                    borderBottom: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    background: activo 
                      ? (colorScheme === 'dark' 
                          ? 'rgba(245, 158, 11, 0.15)'
                          : 'rgba(245, 158, 11, 0.08)')
                      : 'transparent',
                    position: 'relative',
                    cursor: activo ? 'pointer' : 'default',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (activo) {
                      e.currentTarget.style.background = colorScheme === 'dark' 
                        ? 'rgba(245, 158, 11, 0.25)'
                        : 'rgba(245, 158, 11, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activo) {
                      e.currentTarget.style.background = colorScheme === 'dark' 
                        ? 'rgba(245, 158, 11, 0.15)'
                        : 'rgba(245, 158, 11, 0.08)';
                    }
                  }}
                >
                  {esPrimeraFila && (
                    <Text 
                      size="xs" 
                      fw={500} 
                      mb="xs"
                      style={{ 
                        color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                        fontFamily: 'Space Grotesk, Inter, sans-serif'
                      }}
                    >
                      {nombreDia}
                    </Text>
                  )}
                  <Text 
                    size="sm" 
                    fw={activo || esHoy ? 700 : 400}
                    style={{ 
                      color: esHoy
                        ? '#3b82f6'
                        : activo 
                          ? '#f59e0b'
                          : (colorScheme === 'dark' ? '#94a3b8' : '#64748b'),
                      fontFamily: 'Space Grotesk, Inter, sans-serif'
                    }}
                  >
                    {dia}
                  </Text>
                  {activo && (
                    <IconFlame 
                      size={14} 
                      style={{ 
                        position: 'absolute', 
                        top: esPrimeraFila ? '32px' : '8px', 
                        right: '8px', 
                        color: '#f59e0b',
                        fill: '#f59e0b',
                        opacity: 0.9
                      }} 
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EstadisticasPage;
